import dayjs from 'dayjs'
import type { ResAllType } from '../../Types/Api/All'
import type { OKRes } from '../../Types/Api/Ok'
import type {
  ErrorPageContent,
  ICustomPostReq,
  IFilteredPostReq,
  IFilteredPostRes,
  ILayouts,
  IPageLayoutReq,
  IPostTypeRes,
  ITaxonomyRes,
  PageContent,
  PageType,
  SavePageReq,
  UpdatePageReq,
} from '../../Types/Api/Page'
import { api } from './API'
import { isResErrorType, requireAuth } from './Auth'

export const searchPage = async (page: number, name: string, stringSearch) => {
  const res = await requireAuth<
    ResAllType<PageType> & { has_fallback_page: boolean }
  >(
    api.get('pages', {
      queryType: 'search',
      page,
      site_name: name,
      stringSearch: stringSearch,
    }),
  )

  if (!res.ok || !res.data || isResErrorType(res.data)) {
    return res
  }

  return {
    ...res,
    data: {
      ...res.data,
      data: res.data.data.map((d) => ({
        ...d,
        post_date: dayjs(d.post_date),
        created_at: dayjs(d.created_at),
        updated_at: d.updated_at ? dayjs(d.updated_at) : null,
      })),
    },
  }
}

export const findPage = (id: number, name: string, signal?: AbortSignal) => {
  return requireAuth<PageContent>(
    api.get(`pages/${id}`, { site_name: name }, { signal }),
  )
}

export const createPage = (requestValues: SavePageReq) => {
  return requireAuth<PageType>(api.post('pages', requestValues))
}

export const updatePage = (requestValues: UpdatePageReq) => {
  return requireAuth<PageType>(
    api.patch(`pages/${requestValues.id}`, requestValues),
  )
}

export const deletePage = (id: number, name: string) => {
  return requireAuth<OKRes>(api.delete(`pages/${id}`, { site_name: name }))
}

export const duplicatePage = (id: number, name: string) => {
  return requireAuth<OKRes & { id: string }>(
    api.post(`pages/duplicate/${id}`, { site_name: name }),
  )
}

export const createPreview = (requestValues: SavePageReq) => {
  return requireAuth<{ preview_url: string }>(
    api.post('pages/preview', requestValues),
  )
}

export const findPageSectionlist = (name: string) => {
  return requireAuth<any>(api.get(`page_sections`, { site_name: name }))
}

export const findPageSections = (id: string, name: string) => {
  return requireAuth<any>(api.get(`page_sections/${id}`, { site_name: name }))
}

export const findPageLayoutsByType = (type: string, name: string) =>
  requireAuth<ILayouts>(api.get(`pages/layout`, { type, site_name: name }))

export const findPageLayoutTemplate = (
  id: string,
  type: string,
  name: string,
) =>
  requireAuth<{ template: string }>(
    api.get(`pages/layout/${id}`, { type, site_name: name }),
  )

export const createPageLayout = (values: IPageLayoutReq) =>
  requireAuth<{ id: string; ok: boolean }>(api.post(`pages/layout`, values))

export const findAllPostTypes = (site_name: string) =>
  requireAuth<IPostTypeRes[]>(
    api.get(
      `post_types?order_by=asc&queryType=all&site_name=${site_name}&is_paging=0`,
    ),
  )

export const findAllTaxonomies = (
  site_name: string,
  order: string,
  post_type_name: string,
) =>
  requireAuth<ITaxonomyRes[]>(
    api.get(
      `taxonomies/post_type?order_by=${order}&site_name=${site_name}&post_type_name=${post_type_name}`,
    ),
  )

export const findFilteredPosts = (values: IFilteredPostReq) =>
  requireAuth<IFilteredPostRes[]>(api.get(`posts/page`, values))

export const findCustomPosts = (values: ICustomPostReq) =>
  requireAuth<IFilteredPostRes[]>(api.get(`posts/page/custom`, values))

export const findErrorPage = (name: string) =>
  requireAuth<ErrorPageContent>(api.get(`fallback_page`, { site_name: name }))
